import type { TablePaginationConfig } from 'antd';
import { create } from 'zustand';
import type { DefaultApiParams, StorePagination, TableChangeSorter } from '@/std/pagination/types';
import { isObjectEmpty } from '@/std/object';
import { showTotal } from '@/std/pagination/showTotal';

const DEFAULT_PAGE_SIZE = 10;

const paginationStoreResets = new Set<() => void>();

export function resetPaginationStores(): void {
    paginationStoreResets.forEach((reset) => {
        reset();
    });
}

export const DEFAULT_API_PAGINATION: DefaultApiParams = {
    qtde_registros: DEFAULT_PAGE_SIZE,
    registro_inicial: 0,
    total_registros: 'S',
};

export const DEFAULT_TABLE_PAGINATION: TablePaginationConfig = {
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    showTotal,
};

export function getOrderBy<Obj extends Record<string, unknown>>(
    sorter: TableChangeSorter<Obj>,
    fieldMap?: Map<string, string>,
): string | undefined {
    if (Array.isArray(sorter) || isObjectEmpty(sorter as Record<string, unknown>)) {
        return undefined;
    }

    const order = sorter.order === 'descend' ? 'desc' : 'asc';

    if (fieldMap) {
        const mappedField = fieldMap.get(sorter.field as string);
        if (mappedField) {
            return `${mappedField} ${order}`;
        }
    }

    return `${sorter.field as string} ${order}`;
}

function filterOrNull<Filter extends Record<string, unknown>>(
    filter: Partial<Filter> | undefined | null,
): Partial<Filter> | null {
    return filter && !isObjectEmpty(filter) ? filter : null;
}

function scrollToTop() {
    const div = document.getElementById('layout-privado-scroll');
    if (div) {
        div.scrollTo({ behavior: 'smooth', top: 0 });
    }
}

export function createPaginationStore<
    Obj extends Record<string, unknown>,
    Filter extends Record<string, unknown>,
>(props?: {
    filter?: Partial<Filter>;
    apiPagination?: DefaultApiParams;
    fieldMap?: Map<string, string>;
}) {
    const { filter, apiPagination, fieldMap } = props || {};

    const store = create<StorePagination<Obj, Filter>>((set, get) => ({
        apiPagination: {
            ...DEFAULT_API_PAGINATION,
            ...apiPagination,
        },

        filter: filterOrNull<Filter>(filter),

        onTableChange: (pagination, _, sorter) => {
            scrollToTop();

            const page = pagination.current ?? 0;
            const pageSize = pagination.pageSize ?? DEFAULT_PAGE_SIZE;
            const orderby = getOrderBy(sorter, fieldMap);

            set((s) => ({
                apiPagination: {
                    ...s.apiPagination,
                    orderby,
                    qtde_registros: pageSize,
                    registro_inicial: (page - 1) * pageSize,
                    total_registros: 'N',
                },
                tablePagination: {
                    ...s.tablePagination,
                    ...pagination,
                    showTotal,
                },
            }));
        },

        setFilter: (newFilter, replace = false) => {
            const filter = newFilter
                ? replace
                    ? newFilter
                    : { ...get().filter, ...newFilter }
                : null;

            set((s) => ({
                apiPagination: {
                    ...s.apiPagination,
                    registro_inicial: 0,
                    total_registros: 'S',
                },
                filter: filterOrNull<Filter>(filter),
                tablePagination: {
                    ...s.tablePagination,
                    current: 1,
                },
            }));
        },

        setTotalRegistros: (totalRegistros) => {
            set((s) => ({
                apiPagination: {
                    ...s.apiPagination,
                    total_registros: totalRegistros,
                },
            }));
        },

        tablePagination: DEFAULT_TABLE_PAGINATION,

        updateTotalAndPagination: (total: number | undefined) => {
            if (total === undefined || total === null) {
                return;
            }

            // Ajusta a página atual caso o total de registros seja menor que o número de registros que seriam exibidos na página atual

            set((s) => {
                const pageSize = s.tablePagination.pageSize ?? DEFAULT_PAGE_SIZE;
                const totalPages = Math.max(1, Math.ceil(total / pageSize));
                const current = s.tablePagination.current ?? 0;
                const nextCurrent = Math.min(current, totalPages);

                return {
                    apiPagination:
                        nextCurrent !== current
                            ? {
                                  ...s.apiPagination,
                                  registro_inicial: (nextCurrent - 1) * pageSize,
                                  total_registros: 'S',
                              }
                            : s.apiPagination,
                    tablePagination: {
                        ...s.tablePagination,
                        current: nextCurrent,
                        showSizeChanger: total > DEFAULT_PAGE_SIZE,
                        showTotal,
                        total,
                    },
                };
            });
        },
    }));

    paginationStoreResets.add(() => store.setState(store.getInitialState(), true));

    return store;
}
